import styled from "styled-components";
import { tablet } from "styles/breakpoints";

export const RiskResultsContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  width: 100%;
  padding: 20px;

  @media (min-width: ${tablet}px) {
    padding: 24px;
  }
`;

export const HeaderSection = styled.div`
  margin-bottom: 16px;
`;

export const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #4a4d5e;
  text-align: center;

  @media (min-width: ${tablet}px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

export const SubtitleText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #6b6b76;
  text-align: center;

  @media (min-width: ${tablet}px) {
    font-size: 15px;
    line-height: 22px;
  }
`;

export const ContentSection = styled.div`
  margin-bottom: 16px;
`;

export const StatBoxesContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;

  @media (min-width: ${tablet}px) {
    gap: 16px;
  }
`;

export const StatBox = styled.div<{ $type: "action" | "ontrack" }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: ${({ $type }) =>
    $type === "action" ? "rgba(177, 2, 22, 0.08)" : "rgba(74, 140, 59, 0.08)"};
  border: 1px solid ${({ $type }) =>
    $type === "action" ? "rgba(177, 2, 22, 0.15)" : "rgba(74, 140, 59, 0.15)"};
`;

export const StatNumber = styled.div<{ $type: "action" | "ontrack" }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
  background-color: ${({ $type }) =>
    $type === "action" ? "rgba(177, 2, 22, 0.15)" : "rgba(74, 140, 59, 0.15)"};
  color: ${({ $type }) =>
    $type === "action" ? "#B10216" : "#4A8C3B"};
`;

export const StatLabel = styled.span<{ $type: "action" | "ontrack" }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $type }) =>
    $type === "action" ? "#B10216" : "#4A8C3B"};

  @media (min-width: ${tablet}px) {
    font-size: 15px;
  }
`;

export const ActionsSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  padding-top: 8px;
  border-top: 1px solid #EEEEEE;

  @media (min-width: ${tablet}px) {
    gap: 32px;
  }
`;

export const ActionLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: #798ecf;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  text-decoration: none;

  &:hover {
    color: #5a6fb0;
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    text-decoration: underline;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

// Legacy exports for backward compatibility (kept but not used in new design)
export const RiskStatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background-color: transparent;
  border-radius: 8px;
`;

export const RiskStatusBadge = styled.span<{
  $status: "increased" | "decreased" | "average";
}>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  background-color: ${({ $status }) => {
    switch ($status) {
      case "increased":
        return "#EED1D4";
      case "decreased":
        return "#D4EED6";
      default:
        return "#E5E5E5";
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "increased":
        return "#B10216";
      case "decreased":
        return "#1B7D24";
      default:
        return "#616161";
    }
  }};
  gap: 8px;
`;

export const RiskStatusBadgeIcon = styled.span<{
  $status: "increased" | "decreased" | "average";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${({ $status }) => {
    switch ($status) {
      case "increased":
        return "#B10216";
      case "decreased":
        return "#1B7D24";
      default:
        return "#616161";
    }
  }};

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const RiskStatusLabel = styled.p`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #6b6b76;
`;

export const SummaryText = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: #4a4d5e;
  line-height: 1.6;
`;

export const RiskFactorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 16px;

  @media (min-width: ${tablet}px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

export const RiskFactorSection = styled.div`
  border: 1px solid #dedee3;
  border-radius: 8px;
  padding: 12px;

  @media (min-width: ${tablet}px) {
    padding: 16px;
  }
`;

export const RiskFactorTitle = styled.h4<{
  $type: "increasing" | "decreasing";
}>`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #4a4d5e;

  b {
    color: ${({ $type }) =>
      $type === "increasing" ? "#af021c" : "#478c3b"};
  }
`;

export const RiskFactorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

export const RiskFactorItemIncreasing = styled.li`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  background-repeat: no-repeat;
  background-size: 20px 20px;
  background-position: left center;
  padding-left: 28px;
  margin-bottom: 8px;
  color: #4a4d5e;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZyBjbGlwLXBhdGg9InVybCgjY2xpcDBfMjM1MF81Nzc1KSI+CiAgICA8cGF0aCBkPSJNNiAxMEMzLjc5NSAxMCAyIDguMjA1IDIgNkMyIDMuNzk1IDMuNzk1IDIgNiAyQzguMjA1IDIgMTAgMy43OTUgMTAgNkMxMCA4LjIwNSA4LjIwNSAxMCA2IDEwWk02IDExQzguNzYgMTEgMTEgOC43NiAxMSA2QzExIDMuMjQgOC43NiAxIDYgMUMzLjI0IDEgMSAzLjI0IDEgNkMxIDguNzYgMy4yNCAxMSA2IDExWk01LjUgNlY4SDYuNVY2SDhMNiA0TDQgNkg1LjVaIiBmaWxsPSIjQUYwMjFDIi8+CiAgPC9nPgogIDxkZWZzPgogIDxjbGlwUGF0aCBpZD0iY2xpcDBfMjM1MF81Nzc1Ij4KICAgIDxyZWN0IHdpZHRoPSIxMiIgaGVpZ2h0PSIxMiIgZmlsbD0id2hpdGUiLz4KICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KPC9zdmc+Cg==");

  &:last-child {
    margin-bottom: 0;
  }
`;

export const RiskFactorItemDecreasing = styled.li`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  background-repeat: no-repeat;
  background-size: 20px 20px;
  background-position: left center;
  padding-left: 28px;
  margin-bottom: 8px;
  color: #4a4d5e;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMiIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDEyIDEyIiBmaWxsPSJub25lIj4KICA8cGF0aCBkPSJNNiAyQzMuNzk1IDIgMiAzLjc5NSAyIDZDMiA4LjIwNSAzLjc5NSAxMCA2IDEwQzguMjA1IDEwIDEwIDguMjA1IDEwIDZDMTAgMy43OTUgOC4yMDUgMiA2IDJaTTYgMUM4Ljc2IDEgMTEgMy4yNCAxMSA2QzExIDguNzYgOC43NiAxMSA2IDExQzMuMjQgMTEgMSA4Ljc2IDEgNkMxIDMuMjQgMy4yNCAxIDYgMVpNNS41IDZWNEg2LjVWNkg4TDYgOEw0IDZINS41WiIgZmlsbD0iIzQ3OEMzQiIvPgo8L3N2Zz4=");

  &:last-child {
    margin-bottom: 0;
  }
`;

export const EmptyFactors = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #6b6b76;
  font-style: italic;
`;
